import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLComponentTypeManifest } from '../interfaces';
import {
  TpETLPipelineComponent,
  TpETLDryRun,
  TpETLDryRunComponentResult,
  TpETLDryRunMatrixObject,
  isDryRunMatrixObject,
} from '../../interfaces';
import { getETLComponentInputObject, addDryRunObject } from '../../utils';
import { addETLDryRunError } from '../../etlDryRun';
import { CONFIGPARAMTYPES } from '../../../visual-analytics/helpers/configParams';
import { VALTYPES } from '../../../../data/interfaces';
import { createConfigParamTypeMultipleChoice, copyETLDryRunObjectMatrix } from '../utils';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const matrix: TpETLDryRunMatrixObject = {
    type: TpETLObjectTypes.MATRIX,
    id: comp.outputObjectIds['result'],
    createdByCompId: comp.id,
    obsMetadataColumns: [],
    featureMetadataColumns: [],
    linkColumnId: '',
  };
  addDryRunObject(dryRun, matrix);

  const input = getETLComponentInputObject(dryRun, comp, 'source');
  if (!isDryRunMatrixObject(input)) {
    addETLDryRunError(dryRun, `Input is not a matrix`, comp.id, 'source');
    return { abort: false };
  }

  // Set the metadata columns based on the input matrix
  copyETLDryRunObjectMatrix(input, matrix);

  // Add the UMAP projection columns
  const dimension = comp.params['dimension'];
  const components = parseInt(comp.params['components']);

  const featureColumns = [];
  for (let i = 1; i < components + 1; i++) {
    featureColumns.push({
      identifier: `umap_${i}`,
      name: `UMAP ${i}`,
      description: '',
      dataType: VALTYPES.NUMBER,
    });
  }

  // Add the UMAP columns to the appropriate metadata
  if (dimension === 'features') {
    matrix.obsMetadataColumns = [...matrix.obsMetadataColumns, ...featureColumns];
  } else if (dimension === 'observations') {
    matrix.featureMetadataColumns = [...matrix.featureMetadataColumns, ...featureColumns];
  } else {
    addETLDryRunError(dryRun, `Invalid dimension type: ${dimension}`, comp.id, 'dimension');
    return { abort: true };
  }

  return {
    abort: false,
  };
}

export const manifestETLApplyProjectionUmap: TpETLComponentTypeManifest = {
  id: 'ApplyProjectionUmap',
  name: 'UMAP Projection',
  description: 'Perform UMAP projection on matrix data.',
  categoryId: 'matrix',
  icon: <FontAwesomeIcon icon={['fas', 'hexagon-nodes']} />,
  performDryRun,
  inputs: [
    {
      id: 'source',
      name: 'Source matrix',
      type: TpETLObjectTypes.MATRIX,
      description: 'Matrix containing normalized feature observations.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  outputs: [
    {
      type: TpETLObjectTypes.MATRIX,
      id: 'result',
      name: 'Matrix with UMAP',
      description: 'Matrix with UMAP projection applied.',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  params: [
    {
      id: 'dimension',
      name: 'Matrix Dimension',
      description:
        'Dimension to reduce. Selecting observations reduces the rows and adds UMAP components to the feature metadata by transposing the data. ' +
        'Selecting features reduces the columns and adds UMAP components to the observation metadata (no transpose required).',
      paramType: createConfigParamTypeMultipleChoice(
        [
          { id: 'features', name: 'Features' },
          { id: 'observations', name: 'Observations (transpose)' },
        ],
        'observations', // Default to observations
      ),
    },
    {
      id: 'components',
      name: 'Component Count',
      description: 'The dimension of the space to embed into.',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.Numerical,
        defaultValue: 2,
        minVal: 2,
        maxVal: 100,
      },
    },
    {
      id: 'neighbors',
      name: 'Neighbor Count',
      description: 'The size of local neighborhood used for manifold approximation.',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.Numerical,
        defaultValue: 15,
        minVal: 2,
        maxVal: 100,
      },
    },
    {
      id: 'min_dist',
      name: 'Point minimum distance',
      description: 'The effective minimum distance between embedded points.',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.Numerical,
        defaultValue: 0.1,
        minVal: 0.0,
        maxVal: 1.0,
        intervalStep: 0.01,
      },
    },
  ],
};
